import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import TopNav from "../components/TopNav";
import Footer from "../components/Footer";
import API, { formatApiError } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { ArtifactGallery } from "../components/ArtifactGallery";
import { MemberTimeline } from "../components/MemberTimeline";
import { HeritageCareCard } from "../components/HeritageCareCard";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Clock, User, MessageSquare, Send, CheckCircle, XCircle, HelpCircle, ShieldAlert } from "lucide-react";
import logger from "../lib/logger";

const ITEM_LABELS = {
  title: "Title", type: "Type", type_other: "Item Type (Other)",
  approximate_date: "Approximate Date",
  date_of_recording: "Date of Recording", date_of_origin: "Date of Origin",
  date_of_acquisition: "Date of Acquisition",
  location: "Location", people: "People", community: "Community",
  short_description: "Short Description", detailed_description: "Detailed Description",
};
const HISTORICAL_LABELS = {
  importance: "Why is it important?", story: "The story it tells",
  creator: "Creator", is_owner: "Are you the owner?",
  other_owner: "Owner of the item", owner_history: "Previously owned or used by",
  acquired: "How acquired", related_people: "Related people",
  related_places: "Related places / events", additional: "Additional information",
};
const OWNER_LABELS = {
  first_name: "First Name", last_name: "Last Name",
  email: "Email", profile_id: "Profile ID",
};
const CONTACT_LABELS = {
  phone: "Phone", city: "City", country: "Country",
  jamatkhana: "Jamatkhana", preferred_contact: "Preferred Contact",
};

/**
 * Universal submission-detail page. Adapts panels/actions to viewer role.
 */
export default function SubmissionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [history, setHistory] = useState([]);
  const [validations, setValidations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [validators, setValidators] = useState([]);
  const [err, setErr] = useState("");
  const [reload, setReload] = useState(0);

  const isMember = user?.role === "member";
  const isStaff = user?.role === "staff" || user?.role === "admin";
  const isValidator = user?.role === "validator";
  const justSubmitted = params.get("submitted") === "1";

  useEffect(() => {
    (async () => {
      try {
        const [s, h, v, m] = await Promise.all([
          API.get(`/submissions/${id}`),
          API.get(`/submissions/${id}/history`),
          API.get(`/submissions/${id}/validations`),
          API.get(`/messages/${id}`),
        ]);
        setSub(s.data); setHistory(h.data.items); setValidations(v.data.items); setMessages(m.data.items);
        if (isStaff) {
          const uu = await API.get("/users", { params: { role: "validator" } });
          setValidators(uu.data.items || []);
        }
      } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    })();
  }, [id, reload, isStaff]);

  if (err) return (<Wrap><div className="card-heritage text-red-700" data-testid="submission-detail-error">{err}</div></Wrap>);
  if (!sub) return (<Wrap><div>Loading…</div></Wrap>);

  const bumpReload = () => setReload((r) => r + 1);

  return (
    <Wrap>
      <Link to={-1} onClick={(e) => { e.preventDefault(); nav(-1); }} className="text-navy hover:underline text-sm inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Back
      </Link>

      {justSubmitted && isMember && (
        <div className="card-heritage card-attention mb-6" data-testid="just-submitted-banner">
          <div className="font-display text-2xl text-navy mb-1">Thank You for Your Submission</div>
          <div className="text-sm text-ink-soft">
            Submission ID: <span className="font-mono text-ink">{sub.id.slice(0, 8).toUpperCase()}</span> ·
            Item: <strong className="text-ink">{sub.item?.title || "Untitled"}</strong> ·
            Submitted: {sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : "just now"}
          </div>
          <p className="text-sm text-ink-soft mt-2">
            Your information has been received. The Heritage team may contact you if additional information is required.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-widest text-muted-h mb-1">Submission · {sub.id.slice(0, 8).toUpperCase()}</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy mb-3 break-words" data-testid="detail-title">{sub.item?.title || "Untitled Submission"}</h1>
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status={sub.status} memberView={isMember} />
            <div className="text-sm text-muted-h flex items-center gap-1"><Clock size={12} /> Updated {new Date(sub.updated_at).toLocaleString()}</div>
          </div>
        </div>
      </div>

      {isMember && (
        <div className="mb-6 overflow-x-auto">
          <MemberTimeline status={sub.status} />
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6">
          {/* Item info */}
          <div className="card-heritage">
            <h2 className="font-display text-2xl text-ink mb-4">About the Item</h2>
            <DL data={sub.item} labels={ITEM_LABELS} />
          </div>

          {/* Historical */}
          {sub.historical && Object.values(sub.historical).some(v => v) && (
            <div className="card-heritage">
              <h2 className="font-display text-2xl text-ink mb-4">Heritage Context</h2>
              <DL data={sub.historical} labels={HISTORICAL_LABELS} />
            </div>
          )}

          {/* Uploader — staff view only */}
          {!isMember && sub.owner_snapshot && (
            <div className="card-heritage">
              <h2 className="font-display text-2xl text-ink mb-4 flex items-center gap-2"><User size={18} /> Contributor</h2>
              <DL data={sub.owner_snapshot} labels={OWNER_LABELS} />
              {sub.owner_snapshot.profile && Object.keys(sub.owner_snapshot.profile).length > 0 && (
                <>
                  <div className="text-xs uppercase tracking-widest text-muted-h mt-4 mb-2">Contact & Location</div>
                  <DL data={sub.owner_snapshot.profile} labels={CONTACT_LABELS} />
                </>
              )}
            </div>
          )}

          {/* Artifacts — with gallery + lightbox */}
          {sub.artifacts?.length > 0 && (
            <div className="card-heritage">
              <h2 className="font-display text-2xl text-navy mb-4">Uploaded Files</h2>
              <ArtifactGallery artifacts={sub.artifacts} canDownload={!isMember || sub.owner_id === user.id} />
            </div>
          )}

          {/* Privacy concerns — visible to staff only, prominently */}
          {!isMember && sub.permissions?.privacy_concerns && (
            <div className="card-heritage border-l-4 border-yellow-500 bg-yellow-50" data-testid="privacy-concerns">
              <div className="flex items-start gap-3">
                <ShieldAlert size={20} className="text-yellow-700 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-yellow-900">Privacy Concerns from Contributor</div>
                  <p className="text-sm text-yellow-900/90 mt-1 whitespace-pre-wrap">{sub.permissions.privacy_concerns}</p>
                </div>
              </div>
            </div>
          )}

          {/* Validations */}
          {validations.length > 0 && (
            <div className="card-heritage">
              <h2 className="font-display text-2xl text-ink mb-4">Validation Reviews</h2>
              <ul className="space-y-3">
                {validations.map((v) => (
                  <li key={v.id} className="border-l-4 border-[var(--heritage-gold)] pl-3">
                    <div className="text-ink font-medium">{v.validator_name || "Assigned Validator"}</div>
                    <div className="text-sm text-muted-h">Status: {v.status}{v.result ? ` · Result: ${v.result}` : ""}</div>
                    {v.notes && !isMember && <div className="text-sm text-ink-soft mt-1 italic">"{v.notes}"</div>}
                    {v.instructions && !isMember && <div className="text-xs text-muted-h mt-1">Instructions: {v.instructions}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Messages */}
          <MessagesPanel sid={id} messages={messages} isMember={isMember} isStaff={isStaff} onSent={bumpReload} />

          {/* Timeline */}
          {history.length > 0 && (
            <div className="card-heritage">
              <h2 className="font-display text-2xl text-navy mb-4">Activity Timeline</h2>
              <ol className="space-y-3">
                {history.map((h) => (
                  <li key={h.id} className="text-sm text-ink-soft">
                    <span className="text-muted-h">{new Date(h.created_at).toLocaleString()}:</span>{" "}
                    <span className="text-ink">{h.from_status.replace(/_/g, " ")} → {h.to_status.replace(/_/g, " ")}</span>
                    {h.note && !isMember && <span className="text-muted-h"> — {h.note}</span>}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Heritage Item Care — member only, educational */}
          {isMember && <HeritageCareCard defaultOpen={justSubmitted} />}
        </div>

        {/* Actions sidebar */}
        <div className="space-y-4">
          {isMember && sub.status === "more_info_requested" && (
            <MemberRespondCard sid={id} onSent={bumpReload} />
          )}
          {isMember && (sub.status === "draft" || sub.status === "more_info_requested") && (
            <Link to={`/member/submissions/${id}/edit`} className="btn-outline w-full justify-center" data-testid="member-edit">Edit Submission</Link>
          )}
          {isMember && sub.status === "draft" && (
            <SubmitDraftCard sid={id} onSent={bumpReload} />
          )}
          {isValidator && <ValidatorActions sid={id} validations={validations} user={user} onDone={bumpReload} />}
          {isStaff && <StaffActions sid={id} sub={sub} validators={validators} onChange={bumpReload} />}
        </div>
      </div>
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-6xl mx-auto px-6 py-10 flex-1 w-full">{children}</div>
      <Footer />
    </div>
  );
}

function DL({ data, labels }) {
  return (
    <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
      {Object.entries(labels).map(([k, l]) => {
        const v = data?.[k];
        if (v === "" || v == null || v === false) return null;
        const isLong = String(v).length > 60;
        return (
          <div key={k} className={isLong ? "sm:col-span-2" : ""}>
            <dt className="text-muted-h text-xs uppercase tracking-wider">{l}</dt>
            <dd className="text-ink whitespace-pre-wrap mt-0.5">{String(v)}</dd>
          </div>
        );
      })}
    </dl>
  );
}

function MessagesPanel({ sid, messages, isMember, isStaff, onSent }) {
  const [body, setBody] = useState("");
  const [visible, setVisible] = useState(true);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await API.post(`/messages/${sid}`, { body, visible_to_member: isMember || visible });
      setBody(""); toast.success("Message sent"); onSent();
    } catch (e) {
      logger.error("Send message failed:", e);
      toast.error("Could not send message — please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="card-heritage">
      <h2 className="font-display text-2xl text-ink mb-4 flex items-center gap-2"><MessageSquare size={18} /> Messages</h2>
      <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
        {messages.length === 0 && <div className="text-sm text-muted-h italic">No messages yet.</div>}
        {messages.map((m) => (
          <div key={m.id} className={`p-3 rounded border ${m.author_role === "member" ? "border-heritage bg-[var(--heritage-parchment)]" : "border-heritage bg-[var(--heritage-white)]"}`} data-testid={`msg-${m.id}`}>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-medium text-ink">{m.author_name || "Anonymous"}</span>
              <span className="text-xs text-muted-h">{m.author_role}</span>
              {!m.visible_to_member && <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-800">Internal</span>}
              <span className="text-xs text-muted-h ml-auto">{new Date(m.created_at).toLocaleString()}</span>
            </div>
            <div className="text-sm text-ink whitespace-pre-wrap">{m.body}</div>
          </div>
        ))}
      </div>
      <div className="border-t border-heritage pt-4">
        <textarea rows={3} className="input-heritage" placeholder="Write a message…" value={body} onChange={(e) => setBody(e.target.value)} data-testid="message-input" />
        <div className="flex items-center justify-between mt-2">
          {isStaff && (
            <label className="text-xs text-muted-h flex items-center gap-2">
              <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} data-testid="msg-visible-toggle" />
              Visible to community member
            </label>
          )}
          <button onClick={send} disabled={busy || !body.trim()} className="btn-primary ml-auto text-sm" data-testid="message-send">
            <Send size={14} /> Send
          </button>
        </div>
      </div>
    </div>
  );
}

function SubmitDraftCard({ sid, onSent }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await API.post(`/submissions/${sid}/submit`); onSent(); }
    catch (e) { logger.error("Submit draft failed:", e); alert("Could not submit — please try again."); }
    setBusy(false);
  };
  return (
    <div className="card-heritage">
      <div className="font-display text-lg text-ink mb-2">Ready to submit?</div>
      <p className="text-sm text-ink-soft mb-3">Once submitted, the Heritage team will begin reviewing your item.</p>
      <button onClick={submit} disabled={busy} className="btn-primary w-full justify-center" data-testid="submit-draft-btn">
        {busy ? "Submitting…" : "Submit for Review"}
      </button>
    </div>
  );
}

function MemberRespondCard({ sid, onSent }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try { await API.post(`/submissions/${sid}/respond`, { message: msg }); setMsg(""); onSent(); }
    catch (e) { logger.error("Respond failed:", e); alert("Could not send response — please try again."); }
    setBusy(false);
  };
  return (
    <div className="card-heritage border-l-4 border-[var(--heritage-gold)]" data-testid="member-respond-card">
      <div className="font-display text-lg text-ink mb-2">Additional Information Requested</div>
      <p className="text-sm text-ink-soft mb-3">Heritage staff have asked for more details. Please share below.</p>
      <textarea rows={4} className="input-heritage mb-2" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="member-respond-input" />
      <button onClick={send} disabled={busy || !msg.trim()} className="btn-primary w-full justify-center" data-testid="member-respond-btn">Send Response</button>
    </div>
  );
}

function ValidatorActions({ sid, validations, user, onDone }) {
  const val = validations.find((v) => v.validator_id === user.id && v.status !== "completed");
  const [result, setResult] = useState("valid");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  if (!val) return (
    <div className="card-heritage">
      <div className="text-sm text-ink-soft">You have already completed your validation for this item.</div>
    </div>
  );
  const submit = async () => {
    setBusy(true);
    try { await API.post(`/validations/${val.id}/submit`, { result, notes }); onDone(); }
    catch (e) { logger.error("Submit validation failed:", e); alert("Could not submit validation — please try again."); }
    setBusy(false);
  };
  return (
    <div className="card-heritage">
      <div className="font-display text-lg text-ink mb-3">Your Validation</div>
      {val.instructions && <div className="text-sm text-ink-soft bg-[var(--heritage-parchment)] p-2 rounded mb-3">{val.instructions}</div>}
      <div className="space-y-2 mb-3">
        {[
          { v: "valid", l: "Valid", i: CheckCircle },
          { v: "invalid", l: "Invalid", i: XCircle },
          { v: "unknown", l: "Unable to determine", i: HelpCircle },
        ].map((o) => (
          <label key={o.v} className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${result === o.v ? "border-[var(--heritage-terracotta)] bg-[var(--heritage-parchment)]" : "border-heritage"}`}>
            <input type="radio" name="result" checked={result === o.v} onChange={() => setResult(o.v)} data-testid={`validate-${o.v}`} />
            <o.i size={16} /> {o.l}
          </label>
        ))}
      </div>
      <textarea rows={3} className="input-heritage mb-2" placeholder="Validation notes (internal)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="validate-notes" />
      <button onClick={submit} disabled={busy} className="btn-primary w-full justify-center" data-testid="validate-submit">
        {busy ? "Submitting…" : "Submit Validation"}
      </button>
    </div>
  );
}

function StaffActions({ sid, sub, validators, onChange }) {
  const [action, setAction] = useState("");
  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");
  const [validatorId, setValidatorId] = useState("");
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const doStatusChange = async (newStatus) => {
    setBusy(true); setError("");
    try { await API.post(`/submissions/${sid}/status`, { new_status: newStatus, note }); onChange(); }
    catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const doRequestInfo = async () => {
    if (!message.trim()) return;
    setBusy(true); setError("");
    try { await API.post(`/submissions/${sid}/request-info`, { message }); setMessage(""); onChange(); }
    catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const doRequestValidation = async () => {
    if (!validatorId) return;
    setBusy(true); setError("");
    try {
      await API.post(`/validations/request?submission_id=${sid}`,
                     { validator_id: validatorId, instructions });
      setValidatorId(""); setInstructions(""); onChange();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const doPublish = async () => {
    setBusy(true); setError("");
    try {
      // Create collection item then publish
      const { data } = await API.post("/collection", {
        submission_id: sid,
        title: sub.item?.title || "Untitled",
        description: sub.item?.detailed_description || sub.item?.short_description || "",
        historical_context: [sub.historical?.importance, sub.historical?.story]
          .filter(Boolean).join(" "),
        relevant_dates: sub.item?.approximate_date || "",
        location: sub.item?.location || "",
        category: sub.item?.type || "Heritage",
        tags: [sub.item?.community, sub.item?.type].filter(Boolean),
        attribution: `Contributed by ${sub.owner_snapshot?.first_name || ""} ${sub.owner_snapshot?.last_name || ""}`.trim(),
        file_ids: (sub.artifacts || []).map(a => a.file_id).filter(Boolean),
      });
      await API.put(`/collection/${data.id}`, { published: true });
      onChange();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <>
      <div className="card-heritage">
        <div className="font-display text-lg text-ink mb-3">Staff Actions</div>
        {error && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
        <select className="input-heritage mb-3" value={action} onChange={(e) => setAction(e.target.value)} data-testid="staff-action-select">
          <option value="">Choose action…</option>
          <option value="under_review">Move to Under Review</option>
          <option value="request_validation">Request Validation</option>
          <option value="request_info">Request More Info</option>
          <option value="ready_for_approval">Mark Ready for Approval</option>
          <option value="approved">Approve</option>
          <option value="denied">Deny</option>
          <option value="publish">Publish to Collection</option>
        </select>

        {["under_review", "ready_for_approval", "approved", "denied"].includes(action) && (
          <>
            <textarea rows={2} className="input-heritage mb-2" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="staff-note" />
            <button onClick={() => doStatusChange(action)} disabled={busy} className="btn-primary w-full justify-center" data-testid="staff-status-submit">
              Confirm
            </button>
          </>
        )}

        {action === "request_info" && (
          <>
            <textarea rows={4} className="input-heritage mb-2" placeholder="Message to community member" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="staff-request-info-msg" />
            <button onClick={doRequestInfo} disabled={busy || !message.trim()} className="btn-primary w-full justify-center" data-testid="staff-request-info-btn">
              Send Request
            </button>
          </>
        )}

        {action === "request_validation" && (
          <>
            <select className="input-heritage mb-2" value={validatorId} onChange={(e) => setValidatorId(e.target.value)} data-testid="staff-validator-select">
              <option value="">Select validator…</option>
              {validators.map((v) => (
                <option key={v.id} value={v.id} disabled={v.id === sub.owner_id}>
                  {v.first_name} {v.last_name} — {v.profile?.expertise || v.email}
                </option>
              ))}
            </select>
            <textarea rows={3} className="input-heritage mb-2" placeholder="Instructions to validator" value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="staff-validator-instructions" />
            <button onClick={doRequestValidation} disabled={busy || !validatorId} className="btn-primary w-full justify-center" data-testid="staff-request-validation-btn">
              Request Validation
            </button>
          </>
        )}

        {action === "publish" && (
          <>
            <p className="text-sm text-ink-soft mb-2">This will create a Collection entry (editable) and publish it publicly.</p>
            <button onClick={doPublish} disabled={busy} className="btn-primary w-full justify-center" data-testid="staff-publish-btn">
              Publish Now
            </button>
          </>
        )}
      </div>
    </>
  );
}
